import { z } from 'zod';

export const PortalToolNavigationSchema = z.object({
  tabType: z.enum([
    'replay',
    'intercept',
    'match-replace',
    'settings-mcp-connection',
  ]),
  sessionId: z.string().optional(),
  ruleId: z.string().optional(),
  label: z.string().optional(),
  openEdit: z.boolean().optional(),
  identityId: z.string().optional(),
});

export type PortalToolNavigation = z.infer<typeof PortalToolNavigationSchema>;

export const PortalToolResultStatusSchema = z.enum(['success', 'error']);

export type PortalToolResultStatus = z.infer<
  typeof PortalToolResultStatusSchema
>;

export type PortalToolOutputBase = {
  message: string;
  navigation?: PortalToolNavigation;
  data?: Record<string, unknown>;
};

export function isPlainRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function createPortalToolOutputSchema<TData extends z.ZodType>({
  dataSchema,
}: {
  dataSchema: TData;
}) {
  return z.object({
    message: z.string(),
    navigation: PortalToolNavigationSchema.optional(),
    data: dataSchema.optional(),
  });
}

export const PortalToolExecutePayloadSchema = z.object({
  batchId: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.record(z.string(), z.unknown()),
  applicationId: z.string(),
  sessionId: z.string(),
});

export type PortalToolExecutePayload = z.infer<
  typeof PortalToolExecutePayloadSchema
>;

export const PortalToolInterruptPayloadSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  sessionId: z.string(),
});

export type PortalToolInterruptPayload = z.infer<
  typeof PortalToolInterruptPayloadSchema
>;

const PortalToolResultItemEnvelopeSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  status: PortalToolResultStatusSchema,
  result: z.unknown(),
});

const InterruptValueWithPortalPayloadSchema = z.object({
  additional_kwargs: z
    .object({
      portalToolInterrupt: PortalToolInterruptPayloadSchema,
    })
    .optional(),
  portalToolInterrupt: PortalToolInterruptPayloadSchema.optional(),
});

export function parsePortalToolInterruptPayload(
  value: unknown,
): PortalToolInterruptPayload | undefined {
  const parsed = InterruptValueWithPortalPayloadSchema.safeParse(value);
  if (!parsed.success) {
    return undefined;
  }

  return (
    parsed.data.additional_kwargs?.portalToolInterrupt ??
    parsed.data.portalToolInterrupt
  );
}

export function parsePortalToolResultItem<TOutput>({
  value,
  toolCallId,
  toolName,
  outputSchema,
}: {
  value: unknown;
  toolCallId: string;
  toolName: string;
  outputSchema: z.ZodType<TOutput>;
}): { status: PortalToolResultStatus; result: TOutput } | undefined {
  const parsedEnvelope = PortalToolResultItemEnvelopeSchema.safeParse(value);
  if (!parsedEnvelope.success) {
    return undefined;
  }

  const record = parsedEnvelope.data;
  if (record.toolCallId !== toolCallId || record.toolName !== toolName) {
    return undefined;
  }

  const parsedOutput = outputSchema.safeParse(record.result);
  if (!parsedOutput.success) {
    return undefined;
  }

  return {
    status: record.status,
    result: parsedOutput.data,
  };
}
